import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiUrl } from "../config/api";
import { AppShell, CardPanel, FormField, PrimaryButton } from "../components";

const StudentPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState("");
  const [lookupValue, setLookupValue] = useState("");
  const [lookupType, setLookupType] = useState("email");
  const navigate = useNavigate();

  const token = new URLSearchParams(window.location.search).get("token");

  const persistScanContext = (data) => {
    localStorage.setItem("student_id", String(data.student_id));
    localStorage.setItem("student_name", data.student_name);

    if (typeof data.attendance_marked === "boolean") {
      localStorage.setItem(
        "attendance_marked",
        data.attendance_marked ? "true" : "false",
      );
    } else {
      localStorage.removeItem("attendance_marked");
    }

    if (
      typeof data.attendance_status === "string" &&
      data.attendance_status.trim()
    ) {
      localStorage.setItem("attendance_status", data.attendance_status);
    } else {
      localStorage.removeItem("attendance_status");
    }

    if (typeof data.message === "string" && data.message.trim()) {
      localStorage.setItem("attendance_message", data.message);
    } else {
      localStorage.removeItem("attendance_message");
    }
  };

  const generateDailySessionId = () => {
    const today = new Date().toISOString().split("T")[0];
    const sessionKey = `session_id_${today}`;
    let sessionId = localStorage.getItem(sessionKey);

    if (!sessionId) {
      sessionId = `${Math.random().toString(36).slice(2)}-${Date.now()}`;
      localStorage.setItem(sessionKey, sessionId);
    }

    return sessionId;
  };

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing token");
      return;
    }

    const sessionId = generateDailySessionId();
    const existingStudentId = localStorage.getItem("student_id");

    fetch(apiUrl("/api/attendance/scan"), {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token,
        student_id: existingStudentId,
        session_id: sessionId,
      }),
    })
      .then(async (response) => {
        const data = await response.json();

        if (response.ok && data.student_id && data.student_name) {
          persistScanContext(data);
          navigate("/ConfirmationPage");
        } else if (data.requires_registration) {
          // Registration flow should continue on this page.
        } else {
          setError(data.error || "Unknown error");
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Something went wrong while validating token.");
      });
  }, [navigate, token]);

  const handleRegister = async (event) => {
    event.preventDefault();
    setError("");
    const sessionId = generateDailySessionId();

    try {
      const response = await fetch(apiUrl("/api/attendance/scan"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          name,
          email,
          phone,
          session_id: sessionId,
        }),
      });

      const data = await response.json();

      if (response.ok && data.student_id && data.student_name) {
        persistScanContext(data);
        navigate("/ConfirmationPage");
      } else {
        setError(data.error || "Registration failed");
      }
    } catch (err) {
      console.error(err);
      setError("An unexpected error occurred.");
    }
  };

  const handleReturningStudentLookup = async (event) => {
    event.preventDefault();
    setError("");

    const sessionId = generateDailySessionId();

    const payload = {
      token,
      session_id: sessionId,
    };

    if (lookupType === "email") {
      payload.email = lookupValue;
    } else {
      payload.phone = lookupValue;
    }

    try {
      const response = await fetch(apiUrl("/api/attendance/scan"), {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.student_id && data.student_name) {
        persistScanContext(data);
        navigate("/ConfirmationPage");
      } else {
        setError(
          lookupType === "email"
            ? "We could not find this email. Please check the email you registered with and try again."
            : "We could not find this phone number. Please check the phone number you registered with and try again.",
        );
      }
    } catch (err) {
      console.error(err);
      setError("An unexpected error occurred.");
    }
  };
  const handleBackToStudentOptions = () => {
    setMode("");
    setLookupValue("");
    setLookupType("email");
    setName("");
    setEmail("");
    setPhone("");
    setError("");
  };

  return (
    <AppShell
      sidebarImage="/student_avatar.png"
      logoPosition="bottom"
      contentClassName="ui-shell__content--centered"
    >
      <CardPanel className="ui-card-panel--center">
        <div className="ui-auth-card">
          {mode !== "" ? (
            <button
              type="button"
              onClick={handleBackToStudentOptions}
              style={{
                alignSelf: "flex-start",
                background: "none",
                border: "none",
                color: "#0d6efd",
                fontWeight: 600,
                cursor: "pointer",
                textDecoration: "underline",
                marginBottom: "12px",
                padding: 0,
              }}
            >
              ← Back to student options
            </button>
          ) : null}
          <div className="ui-auth-card__head">
            <h1>Attendance for Sunday School</h1>
            <p>Please choose the option that applies to you</p>
          </div>

          {mode === "" ? (
            <div className="ui-auth-card__form">
              <PrimaryButton
                type="button"
                className="ui-auth-submit"
                size="lg"
                onClick={() => setMode("returning")}
              >
                I Am a Returning Student
              </PrimaryButton>

              <PrimaryButton
                type="button"
                className="ui-auth-submit"
                size="lg"
                onClick={() => setMode("new")}
              >
                I Am a New Student
              </PrimaryButton>
            </div>
          ) : mode === "returning" ? (
            <form
              className="ui-auth-card__form"
              onSubmit={handleReturningStudentLookup}
            >
              <FormField
                label={
                  lookupType === "email" ? "Email Address" : "Phone Number"
                }
                id="returningLookup"
                name="returningLookup"
                type={lookupType === "email" ? "email" : "tel"}
                value={lookupValue}
                onChange={(event) => setLookupValue(event.target.value)}
                placeholder={
                  lookupType === "email" ? "johndoe@example.com" : "07012345678"
                }
                required
              />

              <div className="ui-auth-toggle-row">
                {lookupType === "email" ? (
                  <button
                    type="button"
                    onClick={() => {
                      setLookupType("phone");
                      setLookupValue("");
                      setError("");
                    }}
                  >
                    Use Phone Instead
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setLookupType("email");
                      setLookupValue("");
                      setError("");
                    }}
                  >
                    Use Email Instead
                  </button>
                )}
              </div>

              {error ? <p className="ui-error-text">{error}</p> : null}

              <PrimaryButton className="ui-auth-submit" type="submit" size="lg">
                CONTINUE
              </PrimaryButton>
            </form>
          ) : (
            <form className="ui-auth-card__form" onSubmit={handleRegister}>
              <FormField
                label="Full Name"
                id="name"
                name="name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="John Smith"
                required
                autoComplete="name"
              />

              <FormField
                label="Email Address"
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="johndoe@example.com"
                required
                autoComplete="email"
              />

              <FormField
                label="Phone Number"
                id="phone"
                name="phone"
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="07012345678"
                required
                autoComplete="tel"
              />

              {error ? <p className="ui-error-text">{error}</p> : null}
              <p
                style={{
                  fontSize: "13px",
                  lineHeight: "1.6",
                  color: "#6b7280",
                  marginTop: "8px",
                }}
              >
                By continuing, you acknowledge that your information will be
                used for Sunday School attendance and communication purposes in
                line with our{" "}
                <span
                  onClick={() => navigate("/PrivacyNotice")}
                  style={{
                    color: "#0f172a",
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Privacy Notice
                </span>
                .
              </p>
              <PrimaryButton className="ui-auth-submit" type="submit" size="lg">
                SUBMIT
              </PrimaryButton>
            </form>
          )}
        </div>
      </CardPanel>
    </AppShell>
  );
};

export default StudentPage;
